import React, { useState, useEffect } from 'react';
import {
  Bar,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import styled from '@emotion/styled';
import ButtonsGroup from '../common/ButtonsGroup';
import Select from '../common/Select';
import Button from '../common/Button';
import { fetchRecalculateProductMargin } from '../../actions/summary';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { DropDownItem } from '../Orders/SingleOrderPage/MainBlock/ActionsMenuButton';
import { useAutoTranslation } from '../../helpers/useAutoTranslation';

const Wrapper = styled(ResponsiveContainer)`
  .recharts-wrapper .recharts-cartesian-grid-vertical line:nth-last-of-type(-n + 2) {
    stroke-opacity: 0;
  }

  .recharts-tooltip-item {
    padding: 0 !important;
    margin-top: 2px;
  }
`;

const CardWrapper = styled(`div`)`
  .header-title {
    display: flex;
    height: 26px;
    align-items: center;

    .switch-wrapper {
      display: flex;
      font-size: 13px;
      flex-wrap: nowrap;
      align-items: center;
      margin-left: 24px;

      .custom-switch {
        cursor: pointer;
      }
    }
  }

  .select {
    width: 190px;
    margin-left: 20px;
  }

  .more-details {
    cursor: pointer;
    color: rgb(126, 87, 195);
    margin-bottom: 0;
  }

  .additional-info {
    margin-bottom: 0;
    margin-left: 1.55rem;
  }

  .margin-alert {
    display: flex;
    align-items: center;
  }

  .open {
    display: block;
  }

  .close {
    display: none;
  }

  .help-outer-wrapper {
    display: flex;
    justify-content: flex-end;

    .help-tooltip {
      top: -320px;
    }
  }

  @media (min-width: 320px) {
    .header {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: auto;
      grid-template-areas:
        'title about'
        'select select'
        'data data'
        'type type'
        'recalculate recalculate';
      row-gap: 10px;
      margin-bottom: 10px;
    }

    .chart-title {
      grid-area: title;
      font-size: 0.95rem;
      width: 190px;
    }

    .about {
      grid-area: about;
      font-weight: 400;
      font-size: 15.2px;
      justify-self: end;
      align-self: center;
    }

    .select-channels {
      grid-area: select;
      min-width: 150px;
      z-index: 2;
    }

    .data {
      grid-area: data;
    }

    .type {
      grid-area: type;
    }

    .recalculate {
      grid-area: recalculate;
    }
  }

  @media (min-width: 497px) {
    .header {
      grid-template-columns: repeat(3, 1fr);
      column-gap: 5px;
      grid-template-areas:
        'title select about'
        'data data data'
        'type type type'
        'recalculate recalculate recalculate';
    }

    .select-channels {
      width: 135px;
      margin-left: 10px;
    }
  }

  @media (min-width: 660px) {
    .header {
      grid-template-columns: repeat(4, 1fr);
      column-gap: 5px;
      grid-template-areas:
        'title recalculate select about'
        'data data type type';
    }

    .select-channels {
      width: 135px;
      margin-left: 10px;
    }

    .recalculate {
      justify-self: center;
    }
  }

  @media (min-width: 768px) {
    .header {
      column-gap: 10px;
      margin-bottom: 20px;
      grid-template-areas:
        'title recalculate select about'
        'data data type type';
    }

    .select-channels {
      width: auto;
      margin-left: 0;
    }
  }

  @media (min-width: 1400px) {
    .header {
      grid-template-columns: repeat(6, 1fr);
      column-gap: 10px;
      grid-template-areas: 'title recalculate select data type about';
    }

    .select-channels {
      justify-self: center;
    }

    .about {
      justify-self: end;
    }
  }
`;

const MarginSalesChart = (props) => {
  const { title, shipped, delivered } = props;
  const dispatch = useDispatch();

  const [channel, setChannel] = useState(null);
  const [allChannels, setAllChannels] = useState([{ value: null, label: ' - ' }]);
  const [status, setStatus] = useState('shipped');
  const [range, setRange] = useState('monthlyData');
  const [additionalInfo, setAdditionalInfo] = useState({});
  const [loading, setLoading] = useState(false);

  const { t } = useAutoTranslation();

  useEffect(() => {
    if (allChannels) {
      setChannel(allChannels?.[0]);
    }
  }, [allChannels]);

  useEffect(() => {
    defineActualChannels('delivered');
  }, [shipped, delivered]);

  useEffect(() => {
    getCurrentData();
  }, [status, channel, range]);

  const handleStatus = (value) => {
    setStatus(value);
    defineActualChannels(value);
  };

  const handleChannel = (value) => setChannel(value);

  const defineActualChannels = (status) => {
    const channels = props[status]?.channelSales
      .map((item) => ({
        value: item.channel,
        label: item.channel === 'totalSales' ? t('Все каналы') : item.channel,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'ru'));

    setAllChannels(channels);
  };

  const getCurrentData = () => {
    const allChannelSalesByStatus = props[status]?.channelSales;

    if (allChannelSalesByStatus?.length > 0 && channel) {
      const currentChannelData = allChannelSalesByStatus.filter(
        (item) => item.channel === channel.value,
      );

      if (currentChannelData) {
        const currentRangeData = currentChannelData[0]?.[range];
        return currentRangeData;
      } else {
        return false;
      }
    }
  };

  const toggleAdditionalInfo = (infoIdx) => {
    setAdditionalInfo({
      ...additionalInfo,
      [infoIdx]: !additionalInfo[infoIdx],
    });
  };

  const handleRecalculateMargin = async () => {
    setLoading(true);
    const resultDelivered = await dispatch(fetchRecalculateProductMargin(true));
    const resultShipped = await dispatch(fetchRecalculateProductMargin(false));

    if (resultDelivered?.errors?.[0]?.message) {
      toast.error(resultDelivered.errors[0].message);
    }

    if (resultShipped?.errors?.[0]?.message) {
      toast.error(resultShipped.errors[0].message);
    }
    setLoading(false);
  };

  return (
    <div className="col-sm-12">
      <CardWrapper className="card">
        <div className="card-body">
          <header className="header">
            <h4 className="chart-title mr-1">{title}</h4>
            <Select
              className="select-channels"
              options={allChannels}
              value={channel}
              onChange={handleChannel}
              defaultOptions={{ value: null, label: ' - ' }}
            />
            <Button
              className="recalculate"
              type="button"
              text={t('Пересчитать')}
              disabled={loading}
              loading={loading}
              onClick={handleRecalculateMargin}
            />
            <a
              className="about"
              href="https://kak2c.ru/blog/post/v-lichnom-kabinete-kaktusa-poyavilsya-instrument-avtorascheta-marzhinalnosti-prodazh"
              rel="noopener"
              target="_blank"
            >
              {t('Справка')}
            </a>
            <ButtonsGroup
              className="data"
              data={[
                { value: 'weeklyData', label: t('Неделя') },
                { value: 'monthlyData', label: t('Месяц') },
                { value: 'annuallyData', label: t('Год') },
              ]}
              currentValue={range}
              onChange={setRange}
            />
            <ButtonsGroup
              className="type"
              data={[
                { value: 'shipped', label: t('Отгруженные') },
                { value: 'delivered', label: t('Доставленные') },
              ]}
              currentValue={status}
              onChange={handleStatus}
            />
          </header>
          {props?.[status]?.warning?.length > 0 &&
            props[status].warning.map((warn, idx) => {
              return (
                <div key={idx} className="alert alert-warning">
                  <div className="margin-alert">
                    <i className="mdi mdi-alert-outline mr-2"> </i>
                    {warn.title}
                    &nbsp;
                    <p className="more-details" onClick={() => toggleAdditionalInfo(idx)}>
                      {t('Подробнее')}:
                    </p>
                  </div>
                  <p className={`additional-info ${additionalInfo[idx] ? 'open' : 'close'}`}>
                    {warn.additionalInfo}
                  </p>
                </div>
              );
            })}
          <div className="row mt-4 text-center"></div>
          <div className="mt-3">
            <Wrapper width="100%" height={360}>
              <ComposedChart width={830} height={250} data={getCurrentData()}>
                <YAxis />
                <XAxis dataKey="point" type="category" />
                <YAxis yAxisId="right" orientation="right" />
                <Tooltip />
                <Legend iconSize={16} />
                <Bar name={t('Объем продаж')} dataKey="totalSales" fill="#7e57c2" />
                <Bar name={t('Закупка')} dataKey="totalPurchases" fill="#4fc6e1" />
                <Bar name={t('Затраты')} dataKey="totalCosts" fill="#f1556c" />
                <Bar name={t('Валовая прибыль')} dataKey="totalIncome" fill="#1abc9c" />
                <Line
                  type="monotoneX"
                  name={t('Маржинальность, %')}
                  dataKey="margin"
                  stroke="#02a8b5"
                  yAxisId="right"
                />
              </ComposedChart>
            </Wrapper>
            <div className="help-outer-wrapper">
              <div className="help-tooltip-wrapper">
                {t('Описание метрик и правила расчета')} <i className="fe-help-circle" />
                <div className="help-tooltip">
                  <p>
                    <strong>{t('Объем продаж')}</strong> – {t('сумма проданных товаров за период')}
                  </p>
                  <p>
                    <strong>{t('Закупка')}</strong> –{' '}
                    {t('сумма закупочной стоимости проданных товаров за период')}
                  </p>
                  <p>
                    <strong>{t('Затраты')}</strong> –{' '}
                    {t(
                      'сумма затрат, посчитанная на основе данных, указанных в параметрах/номенклатуре',
                    )}
                  </p>
                  <p>
                    <strong>{t('Валовая прибыль')}</strong> –{' '}
                    {t(
                      'прибыль клиента, т.е. выручка за минусом себестоимости реализации, рассчитываемая по формуле: Валовая прибыль = Объем продаж – Закупка – Затраты',
                    )}{' '}
                  </p>
                  <p>
                    <strong>{t('Маржинальность продаж, %')}</strong> –{' '}
                    {t(
                      'общий результат продаж за вычетом основных расходов, рассчитываемая по формуле: Маржинальность продаж, % = (Валовая прибыль/Объем продаж) x 100%',
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardWrapper>
    </div>
  );
};

export default MarginSalesChart;
